import { Button, Row, Table } from "antd";
import { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import CustomerBankAccountsService from "../../../../../../services/customers/bankAccounts";
import { Loader } from "../../../../../../components/Loader";
import { useGetBankAccountsTableColumns } from "./hooks/getBankAccountsTableColumns";
import { useOpenSideBar } from "./hooks/formHandlers/openSideBar";
import { useFetchData } from "../../../../../../lib/hooks/useDefaultFetchData";
import { usePermissions } from "../../../../../../lib/hooks/usePermissions";

type BankAccountsProps = {
  selectedItemId: string;
};

export const BankAccounts = ({ selectedItemId }: BankAccountsProps) => {
  const { hasAccess } = usePermissions();

  const { data, isLoading, fetchData } = useFetchData<any, any>(
    CustomerBankAccountsService,
    selectedItemId,
  );

  const [selectedItem, setSelectedItem] = useState<any>(undefined);

  const isPrimaryDisabled: boolean = useMemo(() => {
    if (selectedItem && data.length === 1) {
      return true;
    }
    return data.length === 0;
  }, [data, selectedItem]);

  const showAddAndNewButton: boolean = useMemo(() => data.length < 1, [data]);

  const { sideBar } = useOpenSideBar(
    selectedItem,
    showAddAndNewButton,
    isPrimaryDisabled,
    selectedItemId,
    setSelectedItem,
    fetchData,
  );

  const onRow = useCallback(
    (record: any) => ({
      onClick: () => setSelectedItem(record),
    }),
    [],
  );

  const { columns } = useGetBankAccountsTableColumns(
    setSelectedItem,
    fetchData,
  );

  const onAddNewClicked = useCallback((): void => setSelectedItem(null), []);

  return (
    <>
      {!isLoading ? (
        <Row>
          <Row className="card-header justify-space-between align-center w-100">
            <h5 className="h h5">Bank Accounts</h5>
            {hasAccess("CustomersBankAccounts", "Add") && (
              <Button
                shape="round"
                onClick={onAddNewClicked}
                disabled={data.length === 2}
                className="btn"
              >
                Add New
              </Button>
            )}
          </Row>
          {data.length > 0 && (
            <Row className="d-block w-100 pt-6">
              <Table
                dataSource={data}
                columns={columns}
                loading={isLoading}
                pagination={false}
                className="overflow-y-auto small-table"
                rowKey="bankAccountNumber"
                onRow={onRow}
              />
            </Row>
          )}
        </Row>
      ) : (
        <Loader fontSize="32px" />
      )}
      {selectedItem !== undefined && createPortal(sideBar, document.body)}
    </>
  );
};
